import React, { useState } from 'react';

import { isArray } from 'lodash';

// components
import ChartCombo, { ChartComboItem } from 'components/charts/chartCombo/chartCombo';
import ChartResetButton from 'components/charts/chartResetButton/chartResetButton';
import ChartInfoButton from 'components/charts/chartInfoButton/chartInfoButton';
import IWPSlider from 'components/charts/slider/slider';

// utils
import chartsUtils, { DataFile } from 'utils/charts.utils';

import BarChartHealth from 'components/charts/barChartHealth/barChartHealth';
import ChartActionsMobile from 'components/charts/chartActionsMobile/chartActionsMobile';
import ChartLegend from 'components/charts/chartLegend/chartLegend';
import ChartTooltip from 'components/charts/chartTooltip/chartTooltip';

interface Props {
  dataFiles: DataFile[];
  titlePrefix: string;
}

const BarChartHealthWrapper = (props: Props) => {
  const getMinYear = () => {
    const years = props.dataFiles.map((dataFile) => chartsUtils.getMinYearForData(dataFile));
    return Math.min(...years);
  };

  const getMaxYear = () => {
    const years = props.dataFiles.map((dataFile) => chartsUtils.getMaxYearForData(dataFile));
    return Math.max(...years);
  };

  const getLocalizations = () => {
    let localizations = chartsUtils.getLocalizationsForData(props.dataFiles[0]);
    if (!localizations) {
      return [];
    }

    localizations = localizations.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));

    let result: Array<ChartComboItem> = [];

    localizations.forEach((localization) => {
      result.push({
        value: localization.code,
        text: localization.name,
      });
    });

    return result;
  };

  const getDefaultLocalization = () => {
    const localizations = getLocalizations();

    const localization = localizations.find((l) => l.value.toLowerCase() == 'ch');
    if (!localization) {
      return localizations[0];
    }

    return localization;
  };

  const [chartRef, setChartRef] = useState<any>();

  const defaultLocalization = getDefaultLocalization();
  const [localization, setLocalization] = useState<ChartComboItem>(defaultLocalization);

  const defaultYear = getMaxYear();
  const [year, setYear] = useState<number>(defaultYear);

  const isResetButtonDisabled = localization.value === defaultLocalization.value && year === defaultYear;

  const [noDataYear, setNoDataYear] = useState<number>(0);

  const handleLocalizationChange = (value: ChartComboItem) => {
    setNoDataYear(0);
    setLocalization(value);
  };

  const handleChangeYear = (value: number | number[]) => {
    setNoDataYear(0);
    if (!isArray(value)) {
      setYear(value);
    } else {
      setYear(value[0]);
    }
  };

  const handleReset = () => {
    setLocalization(getDefaultLocalization());
    setYear(getMaxYear());
    setNoDataYear(0);
  };

  const getNextYearWithData = (year: number) => {
    const years = props.dataFiles.map((dataFile) => chartsUtils.getNextYearWithDataForLocalization(dataFile, year, localization.value));
    return Math.min(...years);
  };

  const handleNoData = () => {
    setNoDataYear(year);
    setYear((year) => getNextYearWithData(year));
  };

  const handleCallback = (chartRef: any) => {
    setChartRef(chartRef);
  };

  const handleExport = () => {
    if (chartRef) {
      chartRef.current.chart.exportChartLocal();
    }
  };

  // TODO: Check Tooltip and Button texts

  return (
    <div className="chart1">
      <div className="chartfilters-wrapper flex flex-wrap gap-x-12 gap-y-4">
        <div className="min-w-[150px] max-w-[200px] flex-1">
          <ChartCombo label="Ort" mode="combo" options={getLocalizations()} onChange={handleLocalizationChange} selectedItem={localization} />
        </div>
        <div className="min-w-[200px] max-w-[300px] flex-1">
          <IWPSlider range={false} min={getMinYear()} max={getMaxYear()} onChange={handleChangeYear} value={year} />
          <ChartTooltip
            show={noDataYear > 0}
            text={`Sie haben ein Jahr ausgewählt, für das keine Daten verfügbar sind. Früher wurde die Steuergrundlage nicht jährlich bemessen. Ihnen wird das nächsthöhere verfügbare Jahr angezeigt.`}
          />
        </div>
        <div className="ml-auto chartfilters-actions">
          <div className="grid-row">
            <div className="grid-cell">
              <ChartResetButton onClick={handleReset} disabled={isResetButtonDisabled} />
            </div>
            <div className="grid-cell">
              <ChartInfoButton text="Geben Sie rechts an, für welches Jahr Sie sich interessieren. Wenn Sie die generierte Grafik herunterladen möchten, klicken Sie bitte auf das Pfeilsymbol links." />
            </div>
          </div>
        </div>
      </div>
      <div className="chart1-wrapper">
        <BarChartHealth
          dataFiles={props.dataFiles}
          title={`${props.titlePrefix} ${localization.text} ${year}`}
          year={year}
          localization={localization}
          onNoData={handleNoData}
          onCallback={handleCallback}
        />
      </div>
      <ChartActionsMobile onExport={handleExport} onReset={handleReset} resetButtonDisabled={isResetButtonDisabled}></ChartActionsMobile>
      <ChartLegend onExport={handleExport} />
    </div>
  );
};

export default BarChartHealthWrapper;
